import i18n from '../i18n';
import { extractProductAttrs, getAffinities, pickGrapeReason } from './userProfile';

function reasonMap(reasons) {
  const m = {};
  for (const r of reasons || []) {
    if (r?.key) m[r.key] = r.value;
  }
  return m;
}

/**
 * @param {{
 *   original: object,
 *   alternative: object,
 *   reasons: { key: string, value: unknown }[],
 *   priceDeltaPct: number,
 * }} input
 * @returns {{ headline: string, body: string }}
 */
export function buildUpsellCopy({ original, alternative, reasons, priceDeltaPct }) {
  const pct = Math.max(1, Math.round((priceDeltaPct || 0) * 100));
  const altAttrs = extractProductAttrs(alternative);
  const rating =
    altAttrs.rating != null && Number.isFinite(altAttrs.rating)
      ? Number(altAttrs.rating).toFixed(1)
      : null;
  const m = reasonMap(reasons);
  const affinities = getAffinities();

  const headline = i18n.t(
    reasons?.length >= 1 || affinities.topCountries.length > 0 || affinities.topGrapes.length > 0
      ? 'upsell.headline_profile'
      : 'upsell.headline_generic'
  );

  const grape =
    pickGrapeReason({
      origCepage: extractProductAttrs(original).cepage,
      candCepage: altAttrs.cepage,
      topGrapes: affinities.topGrapes,
    }) || '';
  const country = m.country ? String(m.country) : '';
  const region = m.region ? String(m.region) : '';

  let body;
  if (grape && m.betterRating != null && rating) {
    body = i18n.t('upsell.body_grape_rating_pct', { grape, rating, pct });
  } else if (grape && country) {
    body = i18n.t('upsell.body_grape_country_pct', { grape, country, pct });
  } else if (grape) {
    body = i18n.t('upsell.body_grape_pct', { grape, pct });
  } else if (country && m.betterRating != null && rating) {
    body = i18n.t('upsell.body_country_rating_pct', { country, rating, pct });
  } else if (country) {
    body = i18n.t('upsell.body_country_pct', { country, pct });
  } else if (m.betterRating != null && rating) {
    body = i18n.t('upsell.body_rating_pct', { rating, pct });
  } else if (m.priceBand) {
    body = i18n.t('upsell.body_price_band_pct', { pct });
  } else if (region) {
    body = i18n.t('upsell.body_region_pct', { region, pct });
  } else {
    body = i18n.t('upsell.body_fallback_pct', {
      name: alternative.name || '',
      pct,
    });
  }

  return { headline, body };
}

/**
 * @param {{
 *   reasons: { key: string, value: unknown }[],
 *   original?: object,
 *   alternative?: object,
 * }} input
 * @returns {string[]}
 */
export function buildUpsellReasonBullets({ reasons, original, alternative }) {
  const m = reasonMap(reasons || []);
  const bullets = [];
  const affinities = getAffinities();
  const grapeLabel =
    original && alternative
      ? pickGrapeReason({
          origCepage: extractProductAttrs(original).cepage,
          candCepage: extractProductAttrs(alternative).cepage,
          topGrapes: affinities.topGrapes,
        }) || ''
      : '';

  if (grapeLabel) {
    bullets.push(i18n.t('upsell.bullet_grape', { grape: grapeLabel }));
  }
  if (m.country) {
    bullets.push(i18n.t('upsell.bullet_country', { country: String(m.country) }));
  }
  if (m.region) {
    bullets.push(i18n.t('upsell.bullet_region', { region: String(m.region) }));
  }
  if (m.betterRating != null) {
    const r = Number(m.betterRating);
    if (Number.isFinite(r)) {
      bullets.push(i18n.t('upsell.bullet_rating', { rating: r.toFixed(1) }));
    }
  }
  if (m.priceBand) {
    bullets.push(i18n.t('upsell.bullet_price_band'));
  }
  if (m.color) {
    bullets.push(i18n.t('upsell.bullet_color', { color: String(m.color) }));
  }

  return bullets.slice(0, 4);
}
